import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { runInServerContext } from '@adornis/baseql/server/server.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { ImportJob } from '@adornis/import-export/db/import-job.js';
import { convertCSVToEntity } from '@adornis/import-export/server/import-internals.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { dispatchSingleWorker } from '@adornis/worker/server/dispatch-single-worker.js';
import { BehaviorSubject, finalize, lastValueFrom, map, tap } from 'rxjs';
import { checkPermissionMiddleware } from '../../../permissions/middlewares/checkPermissionMiddleware.js';
import { Permission } from '../../../permissions/roles.js';
import { PersonImportConvertConfig, type PersonImportData } from '../entities/PersonImportConvertConfig.js';
import {
  ImportCsvCell,
  ImportFindingSeverity,
  PersonImportStrategy,
  type ImportFindingCode,
} from '../entities/PersonImportStrategy.js';
import { PersonImportStrategyValidation } from '../entities/PersonImportStrategyValidation.js';
import type { PersonImportStrategyBuilderJob } from '../PersonImportJobWorkerInterface.js';
import { resolveGetImportStrategies } from '../server/resolver/resolveGetImportStrategies.js';
import { resolveAssignImportPerson, resolveEditImportRow } from '../server/resolver/resolveEditImportStrategy.js';

export const personImportLock = new BehaviorSubject<boolean>(false);

/**
 * @entity PersonImportJob
 */
@Entity()
export class PersonImportJob extends ImportJob<PersonImportData> {
  static override _class = 'PersonImportJob';
  static _collectionName = 'not-used';

  /** the uploaded file's name, handed on to the builder - not a field, the job never leaves the server */
  fileName: string | undefined;

  override async finish(): Promise<void> {
    await runInServerContext(async () => {
      if (personImportLock.value) throw new ExpectedError('Es läuft bereits ein Import-Prozess.');
      personImportLock.next(true);

      try {
        await lastValueFrom(
          dispatchSingleWorker('personImportStrategyBuilder.worker.mjs', {
            importData: this.resultList,
            fileName: this.fileName,
          } satisfies PersonImportStrategyBuilderJob).pipe(
            tap(u => {
              if (typeof u === 'string') this.logger.next(u);

              if (typeof u === 'object' && 'importProcessId' in u) {
                this.logger.next(`Finished: ${u.importProcessId}`);
              }
            }),
          ),
        );
      } finally {
        personImportLock.next(false);
      }
    });
  }
}

/**
 * The generic engine operation would build a plain ImportJob, whose finish() bulk-writes into the
 * entity's collection. The person import has no collection of its own — it hands the parsed rows to
 * the strategy builder — so it has to construct its own PersonImportJob.
 */
const convertPersonImportCSV = registerSubscription({
  operationName: 'convertPersonImportCSV',
  middlewares: [checkPermissionMiddleware(Permission.IMPORT_PERSON, ANY_CONTEXT)],
  type: () => String,
  params: [
    { name: 'csvString', type: () => String },
    { name: 'fileName', type: () => String },
  ],
  resolve: (csvString: string, fileName: Maybe<string>) => () => {
    const convertConfig = new PersonImportConvertConfig({});
    const importJob = new PersonImportJob({ convertConfig });
    importJob.fileName = fileName ?? undefined;
    convertCSVToEntity<PersonImportData>(csvString, convertConfig, importJob);
    return importJob.logger;
  },
});

@Entity()
export class PersonImportStrategyExecutorJob extends AdornisEntity {
  static override _class = 'PersonImportStrategyExecutorJob';

  constructor(doc: PartialEntityData<PersonImportStrategyExecutorJob>) {
    super(doc);
  }

  @Field(() => String)
  importProcessId: Maybe<string>;

  @Field(() => String)
  singleStrategyId: Maybe<string>;

  @Field(() => String)
  sessionID: Maybe<string>;

  @Field(() => String)
  userID: Maybe<string>;
}

@Entity()
export class PersonImportStrategyExecutorResult extends AdornisEntity {
  static override _class = 'PersonImportStrategyExecutorResult';

  constructor(doc: PartialEntityData<PersonImportStrategyExecutorResult>) {
    super(doc);
  }

  @Field(() => Int, { default: v => v ?? 0 })
  entriesInserted: Maybe<number>;

  @Field(() => Int, { default: v => v ?? 0 })
  entriesUpdated: Maybe<number>;

  @Field(() => [String], { default: v => v ?? [] })
  failedStrategyIds: Maybe<string[]>;
}

const executeImportStrategies = registerSubscription({
  operationName: 'executeImportStrategies',
  middlewares: [checkPermissionMiddleware(Permission.IMPORT_PERSON, ANY_CONTEXT)],
  type: () => String,
  params: [
    {
      name: 'job',
      type: () => PersonImportStrategyExecutorJob,
    },
  ],
  resolve: (job: PersonImportStrategyExecutorJob) => () => {
    /**
     * Ensure we know which user triggered the whole thing
     */
    job.sessionID = context.serverContext ? null : context.sessionID;
    job.userID = context.serverContext ? null : context.userID;

    return dispatchSingleWorker<PersonImportStrategyExecutorResult>(
      'personImportStrategyExecutor.worker.mjs',
      job,
    ).pipe(
      tap({ next: () => personImportLock.next(true), error: () => personImportLock.next(false) }),
      map(value => (typeof value === 'string' ? value : JSON.stringify(value))),
      finalize(() => personImportLock.next(false)),
    );
  },
});

/**
 * Both return the row and every guardian row recalculated along with it, so the overview can show their
 * new state without querying the table again.
 */
const assignImportPerson = registerMutation({
  operationName: 'assignImportPerson',
  middlewares: [checkPermissionMiddleware(Permission.IMPORT_PERSON, ANY_CONTEXT)],
  type: () => [PersonImportStrategy],
  params: [
    { name: 'strategyId', type: () => String },
    { name: 'personId', type: () => String },
  ],
  resolve: resolveAssignImportPerson,
});

const editImportRow = registerMutation({
  operationName: 'editImportRow',
  middlewares: [checkPermissionMiddleware(Permission.IMPORT_PERSON, ANY_CONTEXT)],
  type: () => [PersonImportStrategy],
  params: [
    { name: 'strategyId', type: () => String },
    { name: 'cells', type: () => [ImportCsvCell] },
  ],
  resolve: resolveEditImportRow,
});

/**
 * How many rows of one kind still need attention and how many the user has already fixed - one entry
 * per finding code, and one per severity.
 */
@Entity()
export class ImportReviewCount extends AdornisEntity {
  static override _class = 'ImportReviewCount';

  constructor(doc: PartialEntityData<ImportReviewCount>) {
    super(doc);
  }

  @Field(() => ID)
  _id: Maybe<string>;

  /** null for a severity entry */
  @Field(() => String)
  code: Maybe<ImportFindingCode>;

  /** the severity of a severity entry, or the worst open finding of a code entry */
  @Field(() => Int)
  severity: Maybe<ImportFindingSeverity>;

  /** rows that have it right now */
  @Field(() => Int, { default: v => v ?? 0 })
  open: Maybe<number>;

  /** rows that had it in this process and no longer do */
  @Field(() => Int, { default: v => v ?? 0 })
  resolved: Maybe<number>;
}

/**
 * What executing the process would do right now, as three numbers. Live, because the user changes it
 * while looking at it - assigning a person turns an insert into an update, unapproving removes a row
 * from the run.
 */
@Entity()
export class PersonImportStrategyCounts extends AdornisEntity {
  static override _class = 'PersonImportStrategyCounts';

  constructor(doc: PartialEntityData<PersonImportStrategyCounts>) {
    super(doc);
  }

  @Field(() => ID)
  _id: Maybe<string>;

  /** approved rows that resolve to nobody - a person that does not exist yet */
  @Field(() => Int, { default: v => v ?? 0 })
  inserted: Maybe<number>;

  /** approved rows that resolve to somebody and write at least one different value */
  @Field(() => Int, { default: v => v ?? 0 })
  updated: Maybe<number>;

  /** rows whose person is already exactly as the file describes them - see PersonImportStrategy.unchanged */
  @Field(() => Int, { default: v => v ?? 0 })
  unchanged: Maybe<number>;

  /** rows in the overview's table - see PersonImportStrategy.listed */
  @Field(() => Int, { default: v => v ?? 0 })
  listed: Maybe<number>;

  @Field(() => [ImportReviewCount], { default: v => v ?? [] })
  bySeverity: Maybe<ImportReviewCount[]>;

  /** worst open severity first, then the most frequent */
  @Field(() => [ImportReviewCount], { default: v => v ?? [] })
  byCode: Maybe<ImportReviewCount[]>;
}

/**
 * Counted over the rows of the table, the same way the table filters them: a row belongs to a code or a
 * severity for good once it had it (see PersonImportStrategy.reviewCodes), and counts as resolved when it
 * no longer has it.
 */
function countReviews(
  strategies: EntityData<PersonImportStrategy>[],
): Pick<PersonImportStrategyCounts, 'bySeverity' | 'byCode'> {
  const listed = strategies.filter(strategy => strategy.listed);

  const bySeverity = [ImportFindingSeverity.FEHLER, ImportFindingSeverity.WARNUNG].map(severity => {
    const reviewed = listed.filter(strategy => strategy.peakSeverity === severity);
    const open = reviewed.filter(strategy => strategy.severity === severity).length;
    return new ImportReviewCount({ _id: `severity-${severity}`, severity, open, resolved: reviewed.length - open });
  });

  const codes = [...new Set(listed.flatMap(strategy => strategy.reviewCodes ?? []))];
  const byCode = codes
    .map(code => {
      const reviewed = listed.filter(strategy => strategy.reviewCodes?.includes(code));
      const openFindings = reviewed.flatMap(strategy =>
        (strategy.findings ?? []).filter(finding => finding.code === code),
      );
      const open = reviewed.filter(strategy => strategy.findings?.some(finding => finding.code === code)).length;

      return new ImportReviewCount({
        _id: `code-${code}`,
        code,
        severity: Math.max(ImportFindingSeverity.HINWEIS, ...openFindings.map(finding => finding.severity ?? 0)),
        open,
        resolved: reviewed.length - open,
      });
    })
    .sort((a, b) => (b.severity ?? 0) - (a.severity ?? 0) || (b.open ?? 0) - (a.open ?? 0));

  return { bySeverity, byCode };
}

const subscribeImportCounts = registerSubscription({
  operationName: 'subscribeImportCounts',
  middlewares: [checkPermissionMiddleware(Permission.IMPORT_PERSON, ANY_CONTEXT)],
  type: () => PersonImportStrategyCounts,
  params: [
    {
      name: 'processID',
      type: () => String,
    },
  ],
  resolve: (processID: string) => (gqlFields: BaseQLSelectionSet<PersonImportStrategyCounts>) => {
    return watchQuery<PersonImportStrategy>(
      PersonImportStrategy._collectionName,
      { importProcessID: processID },
      {
        projection: {
          approved: 1,
          resolvedId: 1,
          unchanged: 1,
          listed: 1,
          severity: 1,
          peakSeverity: 1,
          reviewCodes: 1,
          'findings.code': 1,
          'findings.severity': 1,
        },
      },
    ).pipe(
      map(
        strategies =>
          new PersonImportStrategyCounts({
            _id: processID,
            inserted: strategies.filter(strategy => strategy.approved && !strategy.resolvedId).length,
            updated: strategies.filter(strategy => strategy.approved && strategy.resolvedId && !strategy.unchanged)
              .length,
            unchanged: strategies.filter(strategy => strategy.unchanged).length,
            listed: strategies.filter(strategy => strategy.listed).length,
            ...countReviews(strategies),
          }),
      ),
    );
  },
});

const subscribeImportValidation = registerSubscription({
  operationName: 'subscribeImportValidation',
  middlewares: [checkPermissionMiddleware(Permission.IMPORT_PERSON, ANY_CONTEXT)],
  type: () => PersonImportStrategyValidation,
  params: [
    {
      name: 'processID',
      type: () => String,
    },
  ],
  resolve: (processID: string) => (gqlFields: BaseQLSelectionSet<PersonImportStrategyValidation>) => {
    return watchQuery<PersonImportStrategyValidation>(
      PersonImportStrategyValidation._collectionName,
      {
        importProcessId: processID,
      },
      {
        projection: gqlFields,
      },
    ).pipe(map(v => v[0]));
  },
});

/**
 * The three latest import processes that can still be opened - see PersonImportStrategyValidation.
 */
const subscribeRecentImports = registerSubscription({
  operationName: 'subscribeRecentImports',
  middlewares: [checkPermissionMiddleware(Permission.IMPORT_PERSON, ANY_CONTEXT)],
  type: () => [PersonImportStrategyValidation],
  params: [],
  resolve: () => (gqlFields: BaseQLSelectionSet<PersonImportStrategyValidation>) =>
    watchQuery<PersonImportStrategyValidation>(
      PersonImportStrategyValidation._collectionName,
      {},
      { projection: gqlFields, sort: { createdAt: -1 }, limit: 3 },
    ),
});

/**
 * Throws an import process away without executing it - its strategies and its record.
 */
const removeImportProcess = registerMutation({
  operationName: 'removeImportProcess',
  middlewares: [checkPermissionMiddleware(Permission.IMPORT_PERSON, ANY_CONTEXT)],
  type: () => Boolean,
  params: [{ name: 'processID', type: () => String }],
  resolve: (processID: string) => async () => {
    if (personImportLock.value) throw new ExpectedError('Während ein Import läuft, kann nichts entfernt werden.');

    await (await getRawCollection(PersonImportStrategy._collectionName)).deleteMany({ importProcessID: processID });
    await (
      await getRawCollection(PersonImportStrategyValidation._collectionName)
    ).deleteOne({ importProcessId: processID });
    return true;
  },
});

/**
 * This is not a subscribtion to prevent faulty rerenders in the dataview. Gravels patching system should later fix this.
 */
const getImportStrategies = registerQuery({
  operationName: 'getImportStrategies',
  middlewares: [checkPermissionMiddleware(Permission.IMPORT_PERSON, ANY_CONTEXT)],
  type: () => [PersonImportStrategy],
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => AdornisFilter, name: 'filter' },
  ],
  resolve: resolveGetImportStrategies,
});

export const PersonImportAPI = {
  convertCSV: convertPersonImportCSV,
  executeStrategies: executeImportStrategies,
  assignPerson: assignImportPerson,
  editRow: editImportRow,
  subscribeValidation: subscribeImportValidation,
  subscribeCounts: subscribeImportCounts,
  getStrategies: getImportStrategies,
  subscribeRecent: subscribeRecentImports,
  removeProcess: removeImportProcess,
};
